import React, { useContext, useEffect, useState } from 'react';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import LazyLoading from '../../../components/LazyLoading';
import { Worker } from '../../../app/models/Workers';
import FundsRequestForm from './forms/FundsRequestForm';
import { defaultFundsRequestDataForm, FundsRequestDataForm, FundRequestReason, defaultWorkerTotal } from '../../../app/models/Fund';
import useFundsRequestService from '../../../app/services/hooks/useFundsRequestService';
import { Currency } from '../../../app/models/Currency';
import { Society } from '../../../app/models/Society';

interface Props {
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
    byReason?: boolean
}

const FundsRequestCreate = ({
    onSaved,
    onCancel,
    onError,
    byReason=false
}: Props) => {
    const [ fundsRequest, setFundsRequest ] = useState<FundsRequestDataForm>({
        ...defaultFundsRequestDataForm,
        worker_total: [ defaultWorkerTotal ]
    });
    const [workers, setWorkers] = useState<Worker[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [requestReasons, setRequestReasons] = useState<FundRequestReason[]>([]);
    const [errorFields, setErrorFields] = useState<any>();
    const [societies, setSocieties] = useState<Society[]>([]);

    const { storeFundsRequest, 
        createFundsRequest, 
        fetchingCreateFundsRequest, 
        fetchingStoreFundsRequest,
        getWorkerSocieties 
    } = useFundsRequestService()

    const { showLoading, hideLoading } = useContext(AppContext);

    useEffect(() => {
        if (showLoading) showLoading('loading', 'Cargando datos...');
        createFundsRequest({
            onSuccess: (response: ServiceResponse) => {
                setRequestReasons(response.data.reason_choices);
                setCurrencies(response.data.currencies);
                setSocieties(response.data.societies);
                setWorkers(response.data.workers);
                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (onError) onError();
            }
        });
    }, []);

    const store = () => {
        const payload: FundsRequestDataForm = byReason && fundsRequest.transfer_reason_id
            ? {
                ...fundsRequest,
                worker_total: fundsRequest.worker_total?.map((item) => ({
                    ...item,
                    transfer_reason_id: fundsRequest.transfer_reason_id
                }))
            }
            : fundsRequest;

        if (showLoading) showLoading('loading', 'Cargando solicitud...');
        storeFundsRequest(payload, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });
                toast.success(response.message, {
                    autoClose: 2500
                });
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    return fetchingCreateFundsRequest ? (
        <LazyLoading height={300} />
    ) : (
        <>
            <FundsRequestForm
                fundsRequest={fundsRequest}
                setFundsRequest={setFundsRequest}
                reasons={requestReasons}
                workers={workers}
                currencies={currencies}
                errorFields={errorFields}
                societies={societies}
                byReason={byReason}
            />
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={cancel} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={store} />
                </div>
            </div>
        </>
    );
};

export default FundsRequestCreate;